// Web Leads (enquiries from website forms, converted into students), Manage Users (the
// workspace's members) and My Account (the signed-in profile).
import { useState } from 'react';

import { CsvPanel, useCsvExport } from 'src/front-components/lead-desk/csv-export';
import { Avatar, DataTable, Drawer, Fact, FactGrid, FilterBar, GroupTitle, Notice, Pager, type Column } from 'src/front-components/lead-desk/kit';
import type { DeskContext } from 'src/front-components/lead-desk/context';
import { PageMasthead, ShellCard, ShellChip } from 'src/front-components/theme/page-chrome';
import { DeskButton, DeskInput, DeskSelect, ErrorLine, LiveStamp } from 'src/front-components/theme/desk-controls';
import { getPalette } from 'src/front-components/theme/ewings-theme';
import { getShellColors, SHELL_RADIUS, SHELL_SPACE, SHELL_TYPE } from 'src/front-components/theme/shell-tokens';
import { convertWebLead, fetchWebLeads } from 'src/front-components/utils/lead-desk-data';
import { useLiveData } from 'src/front-components/utils/desk-data';
import { filterWebLeads, findDuplicate, initials, WEB_SOURCE_LABELS, webLeadsToCsv, paginate, type WebLeadRow } from 'src/utils/lead-desk';

const PAGE_SIZE = 10;
const when = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

export const WebLeadsScreen = ({ ctx }: { ctx: DeskContext }) => {
  const shell = getShellColors(ctx.scheme);
  const live = useLiveData(fetchWebLeads, 30_000);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<WebLeadRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const exporter = useCsvExport();

  const all = live.data?.rows ?? [];
  const rows = filterWebLeads(all, { query, status, source });
  const paged = paginate(rows, page, PAGE_SIZE);
  const existing = open === null ? null : findDuplicate(ctx.students, open.phone);

  const convert = async (): Promise<void> => {
    if (open === null) return;

    setBusy(true);
    setFailure(null);

    try {
      // Converting brings the enquiry onto the desk and gives it, by round robin, to the next counselor and that counselor's next telecaller.
      await convertWebLead(open, '');
      await Promise.all([ctx.refresh(), live.refresh()]);
      setDone(`${open.name ?? 'The enquiry'} is now a student and has been given to the next counselor and telecaller in the round robin.`);
      setOpen(null);
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : 'Could not convert this enquiry.');
    } finally {
      setBusy(false);
    }
  };

  const columns: Column<WebLeadRow>[] = [
    {
      key: 'name',
      header: 'Student Name',
      width: '1.4fr',
      render: (l) => (
        <div>
          <div style={{ ...SHELL_TYPE.bodyMedium, color: shell.text }}>{l.name ?? '—'}</div>
          <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary }}>ID: {l.id.slice(0, 8)}</div>
        </div>
      ),
    },
    { key: 'mobile', header: 'Mobile number', width: '130px', render: (l) => <span style={{ whiteSpace: 'nowrap' }}>{l.phone ?? '—'}</span> },
    {
      key: 'email',
      header: 'Email',
      width: '1.8fr',
      render: (l) => (
        <div style={{ wordBreak: 'break-all', overflowWrap: 'break-word', minWidth: 0, paddingRight: 8 }} title={l.email ?? undefined}>
          {l.email ?? '—'}
        </div>
      ),
    },
    {
      key: 'country',
      header: 'Country / Education',
      width: '1.6fr',
      render: (l) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.countryInterest ?? undefined}>{l.countryInterest ?? '—'}</div>
          <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.education ?? undefined}>Education: {l.education ?? 'N/A'}</div>
        </div>
      ),
    },
    { key: 'submitted', header: 'Submitted At', width: '1.2fr', render: (l) => when(l.submittedAt) },
    { key: 'status', header: 'Status', width: '0.9fr', render: (l) => <ShellChip label={(l.status ?? 'NEW') === 'CONVERTED' ? 'Converted' : 'New'} color={(l.status ?? 'NEW') === 'CONVERTED' ? 'green' : 'sky'} /> },
    { key: 'actions', header: 'Actions', width: '80px', align: 'right', render: (l) => <DeskButton scheme={ctx.scheme} onClick={() => { setOpen(l); setFailure(null); setDone(null); }}>View</DeskButton> },
  ];

  return (
    <>
      <PageMasthead
        palette={getPalette(ctx.scheme)}
        scheme={ctx.scheme}
        title="External Web Leads"
        subtitle="Monitor and process submissions from external web forms and landing pages."
        actions={
          <DeskButton scheme={ctx.scheme} primary onClick={() => exporter.run('web-leads.csv', webLeadsToCsv(rows))}>
            Export to CSV
          </DeskButton>
        }
      />
      <CsvPanel scheme={ctx.scheme} panel={exporter.panel} onClose={exporter.close} />
      {done !== null && <Notice tone="green">{done}</Notice>}
      <ErrorLine scheme={ctx.scheme} message={live.error} />

      <FilterBar scheme={ctx.scheme}>
        <DeskInput scheme={ctx.scheme} value={query} onChange={(value) => { setQuery(value); setPage(1); }} placeholder="Search by name, email, phone, message..." width="340px" />
        <span style={{ ...SHELL_TYPE.label, color: shell.textSecondary }}>Status:</span>
        <DeskSelect scheme={ctx.scheme} width="140px" value={status} onChange={(value) => { setStatus(value); setPage(1); }} options={[{ value: '', label: 'All Statuses' }, { value: 'NEW', label: 'New' }, { value: 'CONVERTED', label: 'Converted' }]} />
        <span style={{ ...SHELL_TYPE.label, color: shell.textSecondary }}>Source:</span>
        <DeskSelect scheme={ctx.scheme} width="150px" value={source} onChange={(value) => { setSource(value); setPage(1); }} options={[{ value: '', label: 'All Sources' }, ...Object.entries(WEB_SOURCE_LABELS).map(([value, label]) => ({ value, label }))]} />
        <DeskButton scheme={ctx.scheme} onClick={() => { setQuery(''); setStatus(''); setSource(''); setPage(1); }}>Reset</DeskButton>
        <LiveStamp scheme={ctx.scheme} at={live.updatedAt} />
      </FilterBar>

      <DataTable scheme={ctx.scheme} columns={columns} rows={paged.items} rowKey={(l) => l.id} empty={live.loading ? 'Loading…' : 'No web enquiries yet. They appear here as soon as a website form is submitted.'} />
      <Pager scheme={ctx.scheme} page={paged.page} pages={paged.pages} onPage={setPage} summary={`Showing page ${paged.page} of ${paged.pages} (${live.data?.total ?? rows.length} total records)`} />

      {open !== null && (
        <Drawer
          scheme={ctx.scheme}
          title="Web Lead Details"
          subtitle={`Lead ID: ${open.id.slice(0, 8)}`}
          onClose={() => setOpen(null)}
          footer={
            <>
              {ctx.profile.canConvertWebLead && (open.status ?? 'NEW') !== 'CONVERTED' && (
                <DeskButton scheme={ctx.scheme} primary disabled={busy || existing !== null} onClick={() => void convert()}>
                  {busy ? 'Converting…' : 'Convert to Student Lead'}
                </DeskButton>
              )}
              <DeskButton scheme={ctx.scheme} onClick={() => setOpen(null)}>Close</DeskButton>
              <ErrorLine scheme={ctx.scheme} message={failure} />
            </>
          }
        >
          <div style={{ display: 'flex', gap: SHELL_SPACE[3], alignItems: 'center', background: shell.backgroundSecondary, padding: SHELL_SPACE[3], borderRadius: SHELL_RADIUS.md }}>
            <Avatar text={initials(open.name)} size={40} />
            <div>
              <div style={{ ...SHELL_TYPE.bodyMedium, color: shell.text }}>{open.name}</div>
              <ShellChip label={(open.status ?? 'NEW') === 'CONVERTED' ? 'Converted' : 'New'} color={(open.status ?? 'NEW') === 'CONVERTED' ? 'green' : 'sky'} />
            </div>
          </div>
          {existing !== null && <div style={{ marginTop: SHELL_SPACE[3] }}><Notice tone="orange">{existing.name ?? 'A student'} already has this number, so this enquiry can't be converted again.</Notice></div>}

          <GroupTitle scheme={ctx.scheme}>Contact Information</GroupTitle>
          <FactGrid><Fact scheme={ctx.scheme} label="Mobile Number" value={open.phone} /><Fact scheme={ctx.scheme} label="Email Address" value={open.email} /></FactGrid>
          <GroupTitle scheme={ctx.scheme}>Education &amp; Preferences</GroupTitle>
          <FactGrid>
            <Fact scheme={ctx.scheme} label="Parent / Student" value={open.parentName} />
            <Fact scheme={ctx.scheme} label="Education Background" value={open.education} />
            <Fact scheme={ctx.scheme} label="Group Interest" value={open.groupInterest} />
            <Fact scheme={ctx.scheme} label="Preferred Country" value={open.countryInterest} />
          </FactGrid>
          <GroupTitle scheme={ctx.scheme}>NEET Status</GroupTitle>
          <FactGrid><Fact scheme={ctx.scheme} label="NEET Qualified Status" value={open.neetQualified} /><Fact scheme={ctx.scheme} label="NEET Score" value={open.neetScore} /></FactGrid>
          <GroupTitle scheme={ctx.scheme}>Marketing &amp; Tracking</GroupTitle>
          <FactGrid><Fact scheme={ctx.scheme} label="Google Click ID (gclid)" value={open.gclid} /><Fact scheme={ctx.scheme} label="Campaign ID" value={open.campaignId} /></FactGrid>
          <GroupTitle scheme={ctx.scheme}>Form Message</GroupTitle>
          <div style={{ ...SHELL_TYPE.body, color: shell.text, whiteSpace: 'pre-wrap', background: shell.backgroundSecondary, padding: SHELL_SPACE[3], borderRadius: SHELL_RADIUS.sm }}>{open.message ?? '—'}</div>
          <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary, marginTop: SHELL_SPACE[3] }}>Submitted on: {when(open.submittedAt)}</div>
        </Drawer>
      )}
    </>
  );
};
